'use client'

import React from 'react'
import { Bot, FileText, Zap, ScanSearch, ArrowDown } from 'lucide-react'

/**
 * AEO Failure Diagrams
 * Visual support for "Why 9 in 10 Websites Fail AEO".
 * Theming per CLAUDE.md: site uses data-theme='dark', NOT Tailwind dark:
 * variants. Use bg-muted/50, bg-background, border-border/60, text-foreground,
 * text-foreground/80, text-muted-foreground. Brand: #008dcc #FDC304 #F90B8A #8b5cf6.
 */

interface Dim {
  label: string
  sub: string
  score: number
  color: string
  icon: React.ReactNode
}

const DIMS: Dim[] = [
  {
    label: 'Crawlability',
    sub: 'Can AI fetch the page',
    score: 64,
    color: '#008dcc',
    icon: <Bot size={18} aria-hidden />,
  },
  {
    label: 'AI Readiness',
    sub: 'Composite technical layer',
    score: 53,
    color: '#8b5cf6',
    icon: <Zap size={18} aria-hidden />,
  },
  {
    label: 'llms.txt',
    sub: 'AI guidance file',
    score: 32,
    color: '#FDC304',
    icon: <FileText size={18} aria-hidden />,
  },
  {
    label: 'AEO',
    sub: 'Can AI answer with the page',
    score: 26,
    color: '#F90B8A',
    icon: <ScanSearch size={18} aria-hidden />,
  },
]

export function AEOReachableAnswerableDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-10 rounded-2xl border border-border/60 bg-muted/50 p-6 md:p-8'
    >
      <div className='mb-6'>
        <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Reachable vs answerable
        </p>
        <p className='mt-1 text-sm leading-relaxed text-foreground/80'>
          Average score by AI visibility dimension across audited domains. Sites
          have largely solved being reachable. Almost none have solved being
          answerable.
        </p>
      </div>

      <div className='space-y-3'>
        {DIMS.map(d => (
          <div
            key={d.label}
            className='rounded-xl border border-border/60 bg-background p-4'
          >
            <div className='flex flex-wrap items-center gap-2 text-foreground'>
              <span style={{ color: d.color }}>{d.icon}</span>
              <span className='text-sm font-semibold leading-none'>
                {d.label}
              </span>
              <span className='text-xs text-muted-foreground'>{d.sub}</span>
              <span
                className='ml-auto font-mono text-sm font-bold'
                style={{ color: d.color }}
              >
                {d.score}/100
              </span>
            </div>
            <div className='mt-3 h-2 w-full overflow-hidden rounded-full bg-muted'>
              <div
                className='h-full rounded-full'
                style={{ width: `${d.score}%`, backgroundColor: d.color }}
              />
            </div>
          </div>
        ))}
      </div>

      <div
        className='mt-5 flex items-center justify-center gap-2 rounded-lg p-4'
        style={{ backgroundColor: '#F90B8A08', border: '1px solid #F90B8A20' }}
      >
        <ArrowDown size={16} aria-hidden style={{ color: '#F90B8A' }} />
        <p className='text-xs leading-relaxed text-foreground/80'>
          <span className='font-semibold' style={{ color: '#F90B8A' }}>
            A 38-point gap
          </span>{' '}
          separates crawlability (64) from AEO (26). That gap is where brands
          lose the AI answer.
        </p>
      </div>
    </div>
  )
}
